import { companies } from '../../data/companies.ts'
import type { Entity } from '../../types/infrastructure'
import type { EditorialCategory, NewsItem } from '../../types/news'
import { matchNewsEvidence } from './evidence.ts'

export const MIN_NEWS_IMAGE_WIDTH = 500
export const MIN_NEWS_IMAGE_HEIGHT = 250

export function isUsableNewsImage(width: number, height: number): boolean {
  if (!Number.isFinite(width) || !Number.isFinite(height)) return false
  if (width < MIN_NEWS_IMAGE_WIDTH || height < MIN_NEWS_IMAGE_HEIGHT) return false
  const aspectRatio = width / height
  return aspectRatio >= 0.55 && aspectRatio <= 4.5
}

export function secondaryNewsCategories(categories: EditorialCategory[]): EditorialCategory[] {
  const primary = categories[0]
  return Array.from(new Set(categories.slice(1).filter((category) => category !== primary)))
}

export function isFeaturedNewsItem(item: NewsItem): boolean {
  return item.featured === true || item.priority === 'featured'
}

export function isPublisherSponsored(item: NewsItem): boolean {
  return /\b(sponsored|advertisement|advertorial|partner content|paid post|brand voice)\b/i.test(`${item.title} ${item.summary ?? ''}`)
}

export function prioritizeReaderNewsItems(items: NewsItem[]): NewsItem[] {
  const editorial = items.filter((item) => !isPublisherSponsored(item))
  const publisherSponsored = items.filter(isPublisherSponsored)
  return [...editorial, ...publisherSponsored]
}

export function balanceNewsItems(items: NewsItem[], sourceInterval = 5): NewsItem[] {
  if (sourceInterval < 1) return items
  const briefings = items.filter((item) => item.kind === 'ai-synthesis')
  const monitored = items.filter((item) => item.kind !== 'ai-synthesis')
  if (!briefings.length || !monitored.length) return items
  const balanced: NewsItem[] = []
  let briefingIndex = 0
  monitored.forEach((item, index) => {
    balanced.push(item)
    if ((index + 1) % sourceInterval === 0 && briefingIndex < briefings.length) balanced.push(briefings[briefingIndex++])
  })
  return [...balanced, ...briefings.slice(briefingIndex)]
}

export function newsItemTickers(item: NewsItem, entities: Entity[] = companies): string[] {
  if (item.tickers?.length) return Array.from(new Set(item.tickers.map((ticker) => ticker.replace(/^\$/, '').toUpperCase())))
  const matches = matchNewsEvidence([item], entities)
  return Array.from(new Set(entities.flatMap((entity) => {
    if (!entity.marketTicker || !matches.get(entity.id)?.some((match) => match.reason === 'entity-name')) return []
    return [entity.marketTicker]
  }))).slice(0, 5)
}
